/**
 * UranIA PA — Root route (US-009 → US-011, esteso US-008 Fase 6).
 *
 * Layout minimale: `<AiDisclosureBanner />` sticky AI Act art. 50
 * (US-011) + `<CutoverAnnouncementBanner />` sticky T-7gg cutover finale
 * (US-008 Fase 6 AC#1) + `<Outlet />` per le route figlie. Niente
 * assunzioni di utente autenticato — pagine protette saranno annidate
 * sotto route con `beforeLoad` guard a partire da US-012/US-013.
 *
 * Ordine sticky (visivo top→bottom):
 *   1. AiDisclosureBanner (compliance permanente AI Act art. 50);
 *   2. CutoverAnnouncementBanner (visibile sse utente in tenant target
 *      + giorno t∈[t_cutover-7gg, t_cutover]; auto-nascosto se
 *      `principal=null` o tenant non in `CUTOVER_SCHEDULE`).
 */

import { createRootRoute, Outlet, ScrollRestoration, useLocation } from '@tanstack/react-router';
import type { JSX } from 'react';

import { AppTopbar } from '../components/AppTopbar.tsx';
import { CutoverAnnouncementBanner } from '../components/cutover/CutoverAnnouncementBanner.tsx';
import { AiDisclosureBanner } from '../components/disclosure/AiDisclosureBanner.tsx';
import { InstitutionalBanner } from '../components/InstitutionalBanner.tsx';
import { selectIsAuthenticated, useAuthStore } from '../lib/auth/auth-store.ts';

// Rotte che gestiscono il proprio layout full-width: il <main> diventa
// un semplice wrapper senza ``container py-4``, cosi' le pagine possono
// disegnare colonne split-screen / hero / etc. Aggiungere nuove rotte
// qui solo quando vogliono *davvero* sovrascrivere il container PA-style.
// ``/`` (copertina "le tre anime", hero full-bleed con fascia notturna —
// assorbe ``/overview``, fusione A.4.1) ci è entrata in Fase 1 del
// redesign vetrina/accesso (2026-09-01); ``/login/pa`` e
// ``/login/beneficiari`` replicano lo split-screen (Fase 3/4, stub per
// ora — vedi routes/login/pa.tsx e routes/login/beneficiari.tsx).
// ``/login`` e ``/overview`` non compaiono qui: sono solo redirect ora
// (fusione A.4.1/A.4.2), non renderizzano mai nulla.
const FULL_WIDTH_ROUTES: readonly string[] = ['/', '/login/pa', '/login/beneficiari'];

// Rotte pubbliche pre-login dell'imbuto vetrina → scelta → login (Fase 1-4
// redesign vetrina/accesso, 2026-09-01; bivio su ``/accedi`` dalla
// fusione A.4.2, era ``/scegli-accesso``). ``/accedi`` non è full-width
// (layout a container standard) ma resta comunque una pagina
// pre-autenticazione: va esclusa dalla striscia istituzionale per lo
// stesso motivo di ``/`` — vedi ``showInstitutionalBanner``.
const PUBLIC_PRE_AUTH_ROUTES: readonly string[] = [...FULL_WIDTH_ROUTES, '/accedi'];

// Rotte autenticate la cui pagina disegna già una propria intestazione
// completa (marchio, ricerca, identità utente) — ``ConsoleLayout``
// (``uc-hdr``, /admin/piattaforma) e ``ServiziPage`` (``uf-hdr``,
// /servizi). Senza questa eccezione la topbar globale si somma alla
// loro, producendo una doppia intestazione visibile (difetto noto,
// segnalato come "cosmetico" ma bloccante per la revisione — non più
// rimandabile come in precedenza per /console). ``/servizi`` disegna
// anche la propria striscia istituzionale (``uf-striscia``): per quella
// rotta va tolta anche la ``InstitutionalBanner`` globale, non solo la
// topbar — vedi ``showInstitutionalBanner`` sotto. ``ConsoleLayout`` non
// replica la striscia istituzionale, quindi per /admin/piattaforma resta
// visibile.
const OWN_CHROME_ROUTES: readonly string[] = ['/admin/piattaforma', '/servizi'];
const OWN_INSTITUTIONAL_STRIP_ROUTES: readonly string[] = ['/servizi'];

// Path su cui NON renderizzare l'AppTopbar globale.
// ``/login`` (UX split-screen senza topbar) + le rotte di
// ``OWN_CHROME_ROUTES`` sopra. Tutte le altre pagine, inclusa l'editor
// OnlyOffice ``/workspace/mod5/<docId>``, mostrano la topbar globale per
// coerenza con il resto della SPA.
function shouldHideTopbar(pathname: string): boolean {
  if (pathname === '/login') return true;
  if (OWN_CHROME_ROUTES.includes(pathname)) return true;
  return false;
}

function RootLayout(): JSX.Element {
  const location = useLocation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isFullWidth = FULL_WIDTH_ROUTES.includes(location.pathname);
  const mainClassName = isFullWidth ? 'urania-app-shell__main--fluid' : 'container py-4';
  const showTopbar = isAuthenticated && !shouldHideTopbar(location.pathname);

  // La striscia istituzionale (Regione Siciliana · AdG FESR/FSE+) appare
  // sempre tranne sulle pagine pubbliche pre-login (vetrina, scelta
  // accesso, entrambi i login), per non anticipare una dicitura
  // istituzionale prima ancora dell'accesso — proprio il problema che la
  // Fase 1 del redesign vetrina/accesso (2026-09-01) vuole risolvere. Su
  // /workspace/mod5/<docId> il Mod5Topbar non duplica piu' la stessa
  // striscia (rimossa, vedi components/workspace/Mod5Topbar.tsx). Stesso
  // motivo per /servizi, che disegna la propria ``uf-striscia`` — vedi
  // OWN_INSTITUTIONAL_STRIP_ROUTES sopra.
  const showInstitutionalBanner =
    !PUBLIC_PRE_AUTH_ROUTES.includes(location.pathname) &&
    !OWN_INSTITUTIONAL_STRIP_ROUTES.includes(location.pathname);

  return (
    <div className="urania-app-shell">
      {/* Senza questo, una navigazione client-side mantiene lo scroll della
          pagina precedente: un form lungo (es. /login/staging) può lasciare
          lo scroll a metà pagina, e sulla pagina successiva il banner AI Act
          sticky finisce sopra la navigazione, rendendola inutilizzabile
          senza scrollare a mano (bug vero, riscontrato su /console). */}
      <ScrollRestoration />
      <AiDisclosureBanner />
      <CutoverAnnouncementBanner />
      {showInstitutionalBanner && <InstitutionalBanner />}
      {showTopbar && <AppTopbar />}
      <main className={mainClassName} lang="it">
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
});
